import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import luxonPlugin from '@fullcalendar/luxon3';
import type {
  DatesSetArg,
  EventClickArg,
  EventDropArg,
  EventInput,
} from '@fullcalendar/core';
import type { EventDragStopArg, EventResizeDoneArg } from '@fullcalendar/interaction';
import { DateTime } from 'luxon';
import { AnimatePresence } from 'motion/react';
import { AlertTriangle, Check, Pencil, Play, Trash2, Undo2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { ContextMenu, type ContextMenuItem } from '@/components/ContextMenu';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { SNAP_MINUTES } from '@/lib/motion';
import { toast } from '@/lib/toast';
import { useIsCompact } from '@/lib/useMediaQuery';
import { useCurrentUser } from '@/features/auth/useAuth';
import { TaskDetail } from '@/features/tasks/TaskDetail';
import {
  useClients,
  useCreateTask,
  useDeleteTask,
  useProjects,
  useTask,
  useTasks,
  useToggleDone,
  useUpdateTask,
  type CreateTaskInput,
} from '@/features/tasks/useTasks';
import { useFocusControls } from '@/features/time/useFocus';
import { FocusBlockPalette } from '@/features/time/FocusBlockPalette';
import { useWorkBlocks, workBlocksKey, type WorkBlock } from '@/features/time/useWorkBlocks';
import type { Task } from '@/features/tasks/types';
import { PlanEventSheet, eventShowsAsBusy, type PlanEvent } from './PlanEventSheet';
import { PlanBacklog, planChipDragStyles } from './PlanBacklog';
import { PlanCarryOver } from './PlanCarryOver';
import { carryTarget } from './carryOver';
import { minutesToSlot, planDayWindow } from './dayWindow';
import { BlockerPalette } from './BlockerPalette';
import { CalendarVisibility } from './CalendarVisibility';
import { PlanQuickAdd } from './PlanQuickAdd';
import type { CaptureIntent } from './quickCapture';
import { BlockerSheet } from './BlockerSheet';
import { EstimateChoiceSheet } from './EstimateChoiceSheet';
import {
  estimateDelta,
  findFreeSlot,
  type EstimateDelta,
  type FreeSlot,
} from './estimateChoice';
import { useCalendarAccounts } from './useCalendars';
import { useDeleteEvent, useToggleEventDone } from './useCalendarEvents';
import {
  useBlockers,
  useCreateBlocker,
  useDeleteBlocker,
  useUpdateBlocker,
} from './useBlockers';
import { dueInfo, isLate, lateBy, type BacklogSort } from './backlogSort';
import { ScheduleSheet } from './ScheduleSheet';
import { ConflictChoiceSheet } from './ConflictChoiceSheet';
import {
  blockTitle,
  buildOccupied,
  canRestoreSingleBlock,
  getDroppedRange,
  intervalConflicts,
  isActiveBlock,
  mergeIntervals,
  newSplitGroupId,
  splitAroundOccupied,
  totalBlockDurationMs,
  type BlockSegment,
} from './scheduleConflict';
import { useStableCalendarEvents } from './useStableCalendarEvents';
import {
  afterCalendarGesture,
  CALENDAR_DRAG_PROPS,
  hasPointerCoords,
  pointerCoords,
} from './calendarDrag';
import {
  BLOCKER_KINDS,
  blockerTitle,
  type Blocker,
  type BlockerKind,
  type BusyInterval,
  type TimeBlock,
} from './types';
import {
  useCarryOverBlocks,
  useConfirmTimeBlock,
  useCreateTimeBlock,
  useDeleteTimeBlock,
  useTimeBlocks,
  useUpdateTimeBlock,
} from './useTimeBlocks';

function pointOverBacklog(clientX: number, clientY: number): boolean {
  const stack = document.elementsFromPoint(clientX, clientY);
  return stack.some((el) => {
    if (!(el instanceof Element)) return false;
    if (el.closest('.fc-event-dragging, .fc-event-mirror, .fc-mirror')) {
      return false;
    }
    return Boolean(el.closest('[data-plan-backlog-drop]'));
  });
}

/**
 * The planner opens on tomorrow.
 *
 * Today is already spent: by the time you are dragging chips onto a grid, the
 * day you are arranging is the next one.
 */
function planningDate(): Date {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 1);
  return date;
}

/** The initial query window, matching the day the grid opens on. */
function dayRangeAround(date: Date): { start: Date; end: Date } {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return { start, end };
}

/** A resize that left the task's planned time out of step with its estimate. */
type EstimateChoicePrompt = {
  taskId: number;
  /** The block that was just resized, the head of any split group. */
  blockId: number;
  taskTitle: string;
  estimateMinutes: number;
  delta: EstimateDelta;
  remainderSlot: FreeSlot | null;
  /** Keeps the continuation in the same split group as the block just resized. */
  splitGroupId: string | null;
};

type PendingPlacement = {
  kind: 'create' | 'move';
  taskId: number;
  taskTitle: string;
  blockId?: number;
  startsAt: Date;
  endsAt: Date;
};

const SORT_KEY = 'chronos.plan.backlog-sort';
const HIDDEN_CALENDARS_KEY = 'chronos.plan.hidden-calendars';
const HIDE_DONE_KEY = 'chronos.plan.hide-completed';

function initialHiddenCalendars(): Set<number> {
  try {
    const raw = JSON.parse(localStorage.getItem(HIDDEN_CALENDARS_KEY) ?? '[]');
    return new Set(Array.isArray(raw) ? raw.filter((id) => typeof id === 'number') : []);
  } catch {
    // A corrupt preference must never cost the user their calendar.
    return new Set();
  }
}

function initialSort(): BacklogSort {
  const stored = localStorage.getItem(SORT_KEY);
  const allowed: BacklogSort[] = ['due', 'effort', 'priority', 'client', 'project'];
  return allowed.includes(stored as BacklogSort) ? (stored as BacklogSort) : 'due';
}

/**
 * Plan view: day grid (tomorrow, by default) + backlog sidebar.
 *
 * Drag a chip onto the grid to create a confirmed time block (write-back is
 * queued server-side). Blocks move/resize; proposed blocks render ghosted.
 */
export function PlanPage() {
  const { data: user } = useCurrentUser();
  const timezone = user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const dayStart = user?.day_start_minute ?? 420;
  const dayEnd = user?.day_end_minute ?? 1200;

  const calendarRef = useRef<FullCalendar>(null);
  const dragMoveCleanupRef = useRef<(() => void) | null>(null);
  const dropHandledRef = useRef(false);
  const dragOriginRef = useRef<{ id: string; start: Date; end: Date } | null>(null);
  const initialDate = useMemo(planningDate, []);
  const [range, setRange] = useState(() => dayRangeAround(initialDate));
  const [selectedEvent, setSelectedEvent] = useState<PlanEvent | null>(null);
  const [selectedBlockId, setSelectedBlockId] = useState<number | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [openTaskId, setOpenTaskId] = useState<number | null>(null);
  const [schedulingTask, setSchedulingTask] = useState<Task | null>(null);
  const [pendingPlacement, setPendingPlacement] = useState<PendingPlacement | null>(null);
  const [estimateChoice, setEstimateChoice] = useState<EstimateChoicePrompt | null>(null);
  const [hiddenCalendars, setHiddenCalendars] =
    useState<Set<number>>(initialHiddenCalendars);
  // Off by default: completed work is part of the picture of the day.
  const [hideCompleted, setHideCompleted] = useState(
    () => localStorage.getItem(HIDE_DONE_KEY) === '1',
  );
  const [dragConflicts, setDragConflicts] = useState(false);
  const [backlogDropActive, setBacklogDropActive] = useState(false);
  const [backlogSort, setBacklogSort] = useState<BacklogSort>(initialSort);
  const [editingBlockerId, setEditingBlockerId] = useState<number | null>(null);
  const [menu, setMenu] = useState<{
    x: number;
    y: number;
    eventId: string;
  } | null>(null);
  const compact = useIsCompact();
  const longPressTimer = useRef<number | null>(null);

  // Phone chrome drops week/month, snap back to day so the grid stays usable.
  useEffect(() => {
    if (!compact) return;
    const api = calendarRef.current?.getApi();
    if (!api) return;
    if (api.view.type !== 'timeGridDay') api.changeView('timeGridDay');
  }, [compact]);

  // Plan opens at the top of the working day, not at "now": the day being
  // arranged is tomorrow's, so the current hour is not the interesting one.
  // The window still stretches to include now, so the indicator stays on-grid.
  const dayWindow = useMemo(
    () => planDayWindow(timezone, dayStart, dayEnd, { anchorToNow: false }),
    [timezone, dayStart, dayEnd],
  );
  const scrollTimeRef = useRef(dayWindow.scrollTime);
  scrollTimeRef.current = dayWindow.scrollTime;

  const clients = useClients();
  const projects = useProjects();
  const accounts = useCalendarAccounts();
  const createTask = useCreateTask();
  const deleteTask = useDeleteTask();
  const toggleDone = useToggleDone();
  const updateTask = useUpdateTask();
  const focusControls = useFocusControls();
  const toggleEventDone = useToggleEventDone();
  const deleteEvent = useDeleteEvent();
  const qc = useQueryClient();
  // Include closed so completed blocks keep "Mark not done" / TaskDetail working.
  const tasks = useTasks({ include_closed: true });
  const blocks = useTimeBlocks(range);
  const carryOver = useCarryOverBlocks(range.start);
  const workBlocksDate =
    DateTime.fromJSDate(range.start, { zone: 'utc' }).setZone(timezone).toISODate() ??
    undefined;
  const workBlocks = useWorkBlocks(workBlocksDate);
  const blockers = useBlockers(range);
  const createBlocker = useCreateBlocker();
  const updateBlocker = useUpdateBlocker();
  const deleteBlocker = useDeleteBlocker();
  const createBlock = useCreateTimeBlock();
  const updateBlock = useUpdateTimeBlock();
  const deleteBlock = useDeleteTimeBlock();
  const confirmBlock = useConfirmTimeBlock();

  const colorFor = useCallback(
    (clientId: number) =>
      clients.data?.find((c) => c.id === clientId)?.color ?? 'var(--border)',
    [clients.data],
  );

  const scrollToNow = useCallback(() => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    const time = scrollTimeRef.current;
    let cancelled = false;
    const tryScroll = (attempt: number) => {
      if (cancelled) return;
      api.scrollToTime(time);
      const scroller = document.querySelector(
        '.chronos-calendar .fc-scroller-liquid-absolute',
      ) as HTMLElement | null;
      // Ready once the time body has laid out (or we've given it enough tries).
      const ready = scroller != null && scroller.scrollHeight > scroller.clientHeight + 8;
      if (ready || attempt >= 10) return;
      window.setTimeout(() => tryScroll(attempt + 1), 40 * (attempt + 1));
    };
    const raf = window.requestAnimationFrame(() => tryScroll(0));
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(raf);
    };
  }, []);

  const onDatesSet = useCallback(
    (arg: DatesSetArg) => {
      setRange((prev) => {
        if (
          prev.start.getTime() === arg.start.getTime() &&
          prev.end.getTime() === arg.end.getTime()
        ) {
          return prev;
        }
        return { start: arg.start, end: arg.end };
      });
      scrollToNow();
    },
    [scrollToNow],
  );

  useEffect(
    () => scrollToNow(),
    [dayWindow.scrollTime, dayWindow.slotMin, dayWindow.slotMax, scrollToNow],
  );

  const events = useQuery({
    queryKey: ['calendars', 'events', range.start.toISOString(), range.end.toISOString()],
    queryFn: () =>
      api<PlanEvent[]>(
        `/api/calendars/events?ends_after=${encodeURIComponent(range.start.toISOString())}` +
          `&starts_before=${encodeURIComponent(range.end.toISOString())}`,
      ),
  });

  const busy = useQuery({
    queryKey: ['calendars', 'busy', range.start.toISOString(), range.end.toISOString()],
    queryFn: () =>
      api<BusyInterval[]>(
        `/api/calendars/busy?start=${encodeURIComponent(range.start.toISOString())}` +
          `&end=${encodeURIComponent(range.end.toISOString())}`,
      ),
  });

  const busyList = busy.data ?? [];
  const blockList = blocks.data ?? [];

  const showGridError =
    (events.isError && !events.data) ||
    (busy.isError && !busy.data) ||
    (blocks.isError && !blocks.data);
  const showBacklogError = tasks.isError && !tasks.data;

  const retryGrid = () => {
    void events.refetch();
    void busy.refetch();
    void blocks.refetch();
  };

  const occupiedForPlacement = useCallback(
    (exclude: { blockId?: number; taskId?: number } = {}) =>
      buildOccupied(busyList, blockList, exclude),
    [busyList, blockList],
  );
  const eventById = useMemo(() => {
    const map = new Map<number, PlanEvent>();
    for (const e of events.data ?? []) map.set(e.id, e);
    return map;
  }, [events.data]);

  const blockById = useMemo(() => {
    const map = new Map<number, TimeBlock>();
    for (const b of blocks.data ?? []) map.set(b.id, b);
    return map;
  }, [blocks.data]);

  /** Due date + title by task id, blocks come from the server without a due date. */
  const taskById = useMemo(() => {
    const map = new Map<number, Task>();
    for (const t of tasks.data ?? []) map.set(t.id, t);
    return map;
  }, [tasks.data]);

  /**
   * Blocks that finish after their task is due.
   *
   * Placing work past its deadline is allowed, sometimes it is the only slot
   * left: but it is never allowed to be invisible.
   */
  const lateBlockIds = useMemo(() => {
    const late = new Set<number>();
    for (const b of blocks.data ?? []) {
      if (b.status === 'completed' || b.status === 'skipped') continue;
      if (isLate(taskById.get(b.task_id)?.due_at ?? null, b.ends_at)) late.add(b.id);
    }
    return late;
  }, [blocks.data, taskById]);

  /** Warn once, in the task's own words, when a placement lands past due. */
  const warnIfLate = useCallback(
    (taskId: number, endsAt: Date | string, fallbackTitle?: string) => {
      const task = taskById.get(taskId);
      const due = task?.due_at ?? null;
      if (!isLate(due, endsAt)) return;
      const title = task?.title ?? fallbackTitle ?? 'This task';
      toast.show(
        `"${title}" is planned ${lateBy(due!, endsAt, timezone)} past its due date`,
        {
          tone: 'warning',
        },
      );
    },
    [taskById, timezone],
  );

  const blockerById = useMemo(() => {
    const map = new Map<number, Blocker>();
    for (const b of blockers.data ?? []) map.set(b.id, b);
    return map;
  }, [blockers.data]);

  const fcEvents: EventInput[] = useMemo(() => {
    // Hidden calendars drop out of the grid only. Their events stay in the
    // busy union, so nothing gets scheduled into time that is actually taken.
    const fromEvents = (events.data ?? [])
      .filter((e) => !hiddenCalendars.has(e.calendar_id))
      .map((e) => {
        const busy = eventShowsAsBusy(e);
        const classes = busy ? [] : ['chronos-event-free'];
        // A ticked-off appointment reads like finished work: still on the day,
        // visibly spent.
        if (e.completed_at) classes.push('chronos-block-done');
        return {
          id: `evt-${e.id}`,
          title: busy ? e.title : `${e.title} (free)`,
          start: e.starts_at,
          end: e.ends_at,
          allDay: e.all_day,
          backgroundColor: busy ? e.color || '#36465f' : 'transparent',
          borderColor: e.color || '#36465f',
          textColor: busy ? '#ffffff' : e.color || '#36465f',
          classNames: classes,
          editable: false,
          startEditable: false,
          durationEditable: false,
        };
      });

    // Finished work stays on the grid by default: a day you can look back at
    // is worth more than a tidy one, and an empty afternoon reads as an
    // afternoon where nothing happened.
    const fromBlocks = (blocks.data ?? [])
      .filter((b) => !(hideCompleted && b.status === 'completed'))
      .map((b) => {
        const proposed = b.status === 'proposed';
        const done = b.status === 'completed';
        const late = lateBlockIds.has(b.id);
        const classes = ['chronos-block'];
        if (proposed) classes.push('chronos-block-proposed');
        if (done) classes.push('chronos-block-done');
        if (late) classes.push('chronos-block-late');
        if (selectedBlockId === b.id) classes.push('ring-2');
        return {
          id: `block-${b.id}`,
          // Said in words, not only in the dashed outline.
          title: blockTitle(b, { late }),
          start: b.starts_at,
          end: b.ends_at,
          backgroundColor: b.client_color,
          borderColor: b.client_color,
          textColor: '#ffffff',
          classNames: classes,
          editable: b.status !== 'completed' && b.status !== 'skipped',
          extendedProps: { kind: 'block', blockId: b.id, status: b.status },
        };
      });

    // The conflict tint is a class on the calendar, never a property of these
    // events. Recolouring them here rebuilt the whole events array in the
    // middle of a drag, and FullCalendar drops the in-flight gesture when its
    // event source is replaced, the drop handler then never runs and the move
    // is silently lost.
    const fromBusy = busyList.map((b, i) => ({
      id: `busy-${i}`,
      start: b.starts_at,
      end: b.ends_at,
      display: 'background' as const,
      classNames: ['chronos-busy'],
    }));

    // Blockers read as a hole in the day rather than a thing on it: no client
    // colour, hatched fill, muted text.
    const fromBlockers = (blockers.data ?? []).map((b) => ({
      id: `blk-${b.id}`,
      title: blockerTitle(b),
      start: b.starts_at,
      end: b.ends_at,
      backgroundColor: 'transparent',
      borderColor: 'var(--border-strong)',
      textColor: 'var(--text-secondary)',
      classNames: ['chronos-blocker'],
      editable: true,
      extendedProps: { kind: 'blocker', blockerId: b.id },
    }));

    const fromWorkBlocks = (workBlocks.data ?? [])
      .filter((wb) => wb.status === 'scheduled' || wb.status === 'active')
      .map((wb) => ({
        id: `wb-${wb.id}`,
        title: wb.status === 'active' ? 'Focus · active' : 'Focus block',
        start: wb.starts_at,
        end: wb.ends_at,
        backgroundColor: 'color-mix(in oklch, var(--accent) 22%, var(--surface-1))',
        borderColor: 'var(--accent)',
        textColor: 'var(--accent)',
        classNames: ['chronos-focus-block'],
        // Always editable so the first drag gesture moves it, no unlock remount.
        editable: true,
        startEditable: true,
        durationEditable: true,
        extendedProps: { kind: 'work_block', workBlockId: wb.id },
      }));

    return [...fromBusy, ...fromWorkBlocks, ...fromEvents, ...fromBlockers, ...fromBlocks];
  }, [
    events.data,
    blocks.data,
    blockers.data,
    workBlocks.data,
    busyList,
    hiddenCalendars,
    selectedBlockId,
    lateBlockIds,
  ]);

  const { displayEvents, beginDrag, endDrag } = useStableCalendarEvents(fcEvents);

  const placeSegments = useCallback(
    async (
      taskId: number,
      segments: BlockSegment[],
      groupId: string,
      replaceBlockId?: number,
    ) => {
      const first = segments[0];
      if (!first) return;
      const rest = segments.slice(1);
      if (replaceBlockId != null) {
        await updateBlock.mutateAsync({
          id: replaceBlockId,
          starts_at: first.startsAt.toISOString(),
          ends_at: first.endsAt.toISOString(),
          is_continuation: first.isContinuation,
          split_group_id: groupId,
        });
      } else {
        await createBlock.mutateAsync({
          task_id: taskId,
          starts_at: first.startsAt.toISOString(),
          ends_at: first.endsAt.toISOString(),
          status: 'confirmed',
          origin: 'manual_drag',
          is_continuation: first.isContinuation,
          split_group_id: groupId,
        });
      }
      for (const seg of rest) {
        await createBlock.mutateAsync({
          task_id: taskId,
          starts_at: seg.startsAt.toISOString(),
          ends_at: seg.endsAt.toISOString(),
          status: 'confirmed',
          origin: 'manual_drag',
          is_continuation: seg.isContinuation,
          split_group_id: groupId,
        });
      }
    },
    [createBlock, updateBlock],
  );

  const commitOverlap = useCallback(
    (pending: PendingPlacement) => {
      if (pending.kind === 'create') {
        createBlock.mutate(
          {
            task_id: pending.taskId,
            starts_at: pending.startsAt.toISOString(),
            ends_at: pending.endsAt.toISOString(),
            status: 'confirmed',
            origin: 'manual_drag',
          },
          {
            onSuccess: () => {
              warnIfLate(pending.taskId, pending.endsAt);
              setPendingPlacement(null);
            },
          },
        );
        return;
      }
      updateBlock.mutate(
        {
          id: pending.blockId!,
          starts_at: pending.startsAt.toISOString(),
          ends_at: pending.endsAt.toISOString(),
          clear_split_group: true,
        },
        {
          onSuccess: () => {
            warnIfLate(pending.taskId, pending.endsAt, pending.taskTitle);
            setPendingPlacement(null);
          },
        },
      );
    },
    [createBlock, updateBlock, warnIfLate],
  );

  const commitSplit = useCallback(
    async (pending: PendingPlacement) => {
      const occupied = occupiedForPlacement(
        pending.kind === 'move'
          ? { blockId: pending.blockId, taskId: pending.taskId }
          : {},
      );
      const segments = splitAroundOccupied(pending.startsAt, pending.endsAt, occupied);
      if (!segments) {
        commitOverlap(pending);
        return;
      }
      const existing =
        pending.blockId != null ? blockById.get(pending.blockId) : undefined;
      const groupId = existing?.split_group_id ?? newSplitGroupId();
      try {
        await placeSegments(pending.taskId, segments, groupId, pending.blockId);
        warnIfLate(pending.taskId, segments.at(-1)!.endsAt, pending.taskTitle);
        setPendingPlacement(null);
      } catch {
        toast.error('Could not split block');
      }
    },
    [blockById, commitOverlap, occupiedForPlacement, placeSegments, warnIfLate],
  );

  const queuePlacement = useCallback(
    (pending: PendingPlacement) => {
      const occupied = occupiedForPlacement(
        pending.kind === 'move'
          ? { blockId: pending.blockId, taskId: pending.taskId }
          : {},
      );
      if (!intervalConflicts(pending.startsAt, pending.endsAt, occupied)) {
        commitOverlap(pending);
        return;
      }
      setPendingPlacement(pending);
    },
    [commitOverlap, occupiedForPlacement],
  );

  const applyMove = useCallback(
    (blockId: number, taskId: number, taskTitle: string, start: Date, end: Date) => {
      const siblings = blockList.filter(
        (b) => b.task_id === taskId && b.id !== blockId && isActiveBlock(b.status),
      );
      const occupied = occupiedForPlacement({ blockId, taskId });

      if (siblings.length > 0) {
        const totalMs = totalBlockDurationMs(blockList, taskId);
        if (canRestoreSingleBlock(start, totalMs, occupied)) {
          updateBlock.mutate(
            {
              id: blockId,
              starts_at: start.toISOString(),
              ends_at: new Date(start.getTime() + totalMs).toISOString(),
              clear_split_group: true,
            },
            {
              onSuccess: () => {
                for (const s of siblings) deleteBlock.mutate(s.id);
                warnIfLate(taskId, new Date(start.getTime() + totalMs), taskTitle);
              },
            },
          );
          return;
        }
      }

      updateBlock.mutate(
        {
          id: blockId,
          starts_at: start.toISOString(),
          ends_at: end.toISOString(),
        },
        {
          onSuccess: () => warnIfLate(taskId, end, taskTitle),
        },
      );
    },
    [blockList, deleteBlock, occupiedForPlacement, updateBlock, warnIfLate],
  );

  const placeBlock = useCallback(
    (taskId: number, startsAt: Date, endsAt: Date) => {
      const task = taskById.get(taskId);
      queuePlacement({
        kind: 'create',
        taskId,
        taskTitle: task?.title ?? 'Task',
        startsAt,
        endsAt,
      });
    },
    [queuePlacement, taskById],
  );

  /**
   * Create a blocker at a dropped slot.
   *
   * The generic "Blocker" kind opens the sheet straight after, because that is
   * the one case where the name is the whole point; Lunch already knows what
   * it is called.
   */
  const placeBlocker = useCallback(
    (kind: BlockerKind, startsAt: Date, minutes?: number) => {
      const preset = BLOCKER_KINDS.find((k) => k.value === kind);
      const length = minutes ?? preset?.minutes ?? 30;
      createBlocker.mutate(
        {
          kind,
          starts_at: startsAt.toISOString(),
          ends_at: new Date(startsAt.getTime() + length * 60_000).toISOString(),
        },
        {
          onSuccess: (created) => {
            if (kind === 'other') setEditingBlockerId(created.id);
          },
        },
      );
    },
    [createBlocker],
  );

  const placeFocusBlock = useCallback((startsAt: Date, minutes = 120) => {
    void api('/api/work-blocks', {
      method: 'POST',
      json: {
        starts_at: startsAt.toISOString(),
        ends_at: new Date(startsAt.getTime() + minutes * 60_000).toISOString(),
        origin: 'plan_scheduled',
      },
    }).then(() => {
      void workBlocks.refetch();
      toast.show('Focus block scheduled', { tone: 'success' });
    });
  }, [workBlocks]);

  const startWorkBlock = useCallback((blockId: number) => {
    void api(`/api/work-blocks/${blockId}/start`, { method: 'POST' })
      .then(() => {
        void workBlocks.refetch();
        toast.show('Work block started', { tone: 'success' });
      })
      .catch((err) => toast.error(err instanceof Error ? err.message : 'Could not start'));
  }, [workBlocks]);

  const removeWorkBlock = useCallback((blockId: number) => {
    void api(`/api/work-blocks/${blockId}`, { method: 'DELETE' })
      .then(() => {
        void workBlocks.refetch();
        toast.show('Focus block removed', { tone: 'success' });
      })
      .catch((err) => toast.error(err instanceof Error ? err.message : 'Could not remove'));
  }, [workBlocks]);

  const workBlockById = useMemo(() => {
    const map = new Map<number, WorkBlock>();
    for (const wb of workBlocks.data ?? []) map.set(wb.id, wb);
    return map;
  }, [workBlocks.data]);

  const removeBlocker = useCallback(
    (blocker: Blocker) => {
      deleteBlocker.mutate(blocker.id, {
        onSuccess: () => {
          setEditingBlockerId(null);
          toast.undoable(`Removed "${blockerTitle(blocker)}"`, () => {
            createBlocker.mutate({
              kind: blocker.kind,
              label: blocker.label,
              starts_at: blocker.starts_at,
              ends_at: blocker.ends_at,
            });
          });
        },
      });
    },
    [createBlocker, deleteBlocker],
  );

  const persistHidden = useCallback((next: Set<number>) => {
    setHiddenCalendars(next);
    localStorage.setItem(HIDDEN_CALENDARS_KEY, JSON.stringify([...next]));
  }, []);

  const toggleCalendar = useCallback(
    (calendarId: number) => {
      const next = new Set(hiddenCalendars);
      if (next.has(calendarId)) next.delete(calendarId);
      else next.add(calendarId);
      persistHidden(next);
    },
    [hiddenCalendars, persistHidden],
  );

  const completedCount = useMemo(
    () => (blocks.data ?? []).filter((b) => b.status === 'completed').length,
    [blocks.data],
  );

  /** Calendars Chronos may delete from, its own, plus opted-in write targets. */
  const deletableCalendarIds = useMemo(() => {
    const ids = new Set<number>();
    for (const account of accounts.data ?? []) {
      for (const calendar of account.calendars) {
        if (
          account.provider === 'chronos' ||
          (calendar.is_write_target && !calendar.read_only)
        ) {
          ids.add(calendar.id);
        }
      }
    }
    return ids;
  }, [accounts.data]);

  /** Create a task on the dragged slot, place it, then open the full editor. */
  const createTaskOnSlot = useCallback(
    (slot: { start: Date; end: Date }) => {
      const minutes = Math.max(
        5,
        Math.round((slot.end.getTime() - slot.start.getTime()) / 60_000),
      );
      const clientId = clients.data?.[0]?.id;
      if (clientId == null) {
        toast.error('Add a client in Settings before scheduling tasks');
        return;
      }
      createTask.mutate(
        {
          title: 'New task',
          client_id: clientId,
          estimate_minutes: minutes,
        },
        {
          onSuccess: (task) => {
            queuePlacement({
              kind: 'create',
              taskId: task.id,
              taskTitle: task.title,
              startsAt: slot.start,
              endsAt: slot.end,
            });
            setOpenTaskId(task.id);
            toast.undoable(`Added "${task.title}"`, () => deleteTask.mutate(task.id));
          },
          onError: (error) => toast.error(error.message),
        },
      );
    },
    [clients.data, createTask, deleteTask, queuePlacement],
  );

  /**
   * Capture from the sidebar: file it, or place it when a time was given.
   *
   * Both paths end in an undo toast, capture is fast on purpose, and anything
   * fast enough to be useful is fast enough to be wrong.
   */
  const captureTask = useCallback(
    (input: CreateTaskInput, intent: CaptureIntent) => {
      createTask.mutate(input, {
        onSuccess: (task) => {
          if (intent.kind === 'schedule' && intent.startsAt && intent.endsAt) {
            // Same path as a drag onto the grid, so a clash asks the same
            // question instead of quietly stacking on top of a meeting.
            queuePlacement({
              kind: 'create',
              taskId: task.id,
              taskTitle: task.title,
              startsAt: intent.startsAt,
              endsAt: intent.endsAt,
            });
            // Placement may still be waiting on a conflict answer, so the
            // toast claims only what already happened.
            toast.undoable(`Added "${task.title}"`, () => deleteTask.mutate(task.id));
            return;
          }
          toast.undoable(`Added "${task.title}" to the backlog`, () =>
            deleteTask.mutate(task.id),
          );
        },
        onError: (error) => toast.error(error.message),
      });
    },
    [createTask, deleteTask, queuePlacement],
  );

  const returnBlockToBacklog = useCallback(
    (block: TimeBlock) => {
      const snapshot = { ...block };
      deleteBlock.mutate(block.id, {
        onSuccess: () => {
          setSelectedBlockId(null);
          setOpenTaskId(null);
          toast.undoable(`Returned "${block.task_title}" to backlog`, () => {
            createBlock.mutate({
              task_id: snapshot.task_id,
              starts_at: snapshot.starts_at,
              ends_at: snapshot.ends_at,
              status: snapshot.status === 'proposed' ? 'proposed' : 'confirmed',
              origin: snapshot.origin,
            });
          });
        },
      });
    },
    [createBlock, deleteBlock],
  );

  const onDrop = useCallback(
    (info: { date: Date; draggedEl: HTMLElement; allDay: boolean }) => {
      if (info.allDay) return;

      const blockerKind = info.draggedEl.dataset.blockerKind as BlockerKind | undefined;
      if (blockerKind) {
        placeBlocker(
          blockerKind,
          info.date,
          Number(info.draggedEl.dataset.minutes) || undefined,
        );
        setDragConflicts(false);
        return;
      }

      if (info.draggedEl.hasAttribute('data-focus-block-chip')) {
        placeFocusBlock(
          info.date,
          Number(info.draggedEl.dataset.minutes) || 120,
        );
        setDragConflicts(false);
        return;
      }

      const taskId = Number(info.draggedEl.dataset.taskId);
      if (!Number.isFinite(taskId)) return;
      const minutes = Number(info.draggedEl.dataset.minutes) || SNAP_MINUTES * 2;
      const start = info.date;
      const end = new Date(start.getTime() + minutes * 60_000);

      // Leftover work is already a block. Dropping it forward moves that
      // block; creating a second one would leave the abandoned day still
      // looking like a day where the work is scheduled.
      const carryBlockId = Number(info.draggedEl.dataset.blockId);
      if (info.draggedEl.hasAttribute('data-carry-chip') && Number.isFinite(carryBlockId)) {
        queuePlacement({
          kind: 'move',
          blockId: carryBlockId,
          taskId,
          taskTitle: info.draggedEl.dataset.title ?? 'Task',
          startsAt: start,
          endsAt: end,
        });
        setDragConflicts(false);
        return;
      }

      placeBlock(taskId, start, end);
      setDragConflicts(false);
    },
    [placeBlock, placeBlocker, placeFocusBlock, queuePlacement],
  );

  const persistWorkBlockTimes = useCallback(
    (blockId: number, start: Date, end: Date, onError?: () => void) => {
      const starts_at = start.toISOString();
      const ends_at = end.toISOString();
      // Optimistic: locking / refetch must not snap the band back to the old slot.
      qc.setQueryData<WorkBlock[]>(workBlocksKey(workBlocksDate), (prev) =>
        prev?.map((wb) => (wb.id === blockId ? { ...wb, starts_at, ends_at } : wb)),
      );
      void api(`/api/work-blocks/${blockId}`, {
        method: 'PATCH',
        json: { starts_at, ends_at },
      })
        .then(() => void workBlocks.refetch())
        .catch((err) => {
          void workBlocks.refetch();
          toast.error(err instanceof Error ? err.message : 'Could not move focus block');
          onError?.();
        });
    },
    [qc, workBlocks, workBlocksDate],
  );

  const onEventDrop = useCallback(
    (arg: EventDropArg) => {
      dropHandledRef.current = true;
      const id = arg.event.id;
      const { x, y } = pointerCoords(arg.jsEvent);
      if (id.startsWith('wb-')) {
        const range = getDroppedRange(arg);
        if (!range) {
          arg.revert();
          return;
        }
        persistWorkBlockTimes(Number(id.slice(3)), range.start, range.end, () => arg.revert());
        return;
      }
      if (id.startsWith('blk-')) {
        const range = getDroppedRange(arg);
        if (!range) {
          arg.revert();
          return;
        }
        const { start, end } = range;
        // Dropping over the backlog is handled in eventDragStop, which already
        // removed it. Nothing to persist, and nothing to revert either.
        if (hasPointerCoords(arg.jsEvent) && pointOverBacklog(x, y)) return;

        updateBlocker.mutate(
          {
            id: Number(id.slice(4)),
            starts_at: start.toISOString(),
            ends_at: end.toISOString(),
          },
          {
            onError: () => arg.revert(),
          },
        );
        return;
      }
      if (!id.startsWith('block-')) {
        arg.revert();
        return;
      }
      if (hasPointerCoords(arg.jsEvent) && pointOverBacklog(x, y)) {
        arg.revert();
        return;
      }
      const blockId = Number(id.slice(6));
      const range = getDroppedRange(arg);
      if (!range) {
        arg.revert();
        return;
      }
      const { start, end } = range;
      setDragConflicts(false);
      const block = blockById.get(blockId);
      if (!block) {
        arg.revert();
        return;
      }
      if (
        intervalConflicts(
          start,
          end,
          occupiedForPlacement({ blockId, taskId: block.task_id }),
        )
      ) {
        arg.revert();
        setPendingPlacement({
          kind: 'move',
          taskId: block.task_id,
          taskTitle: block.task_title,
          blockId,
          startsAt: start,
          endsAt: end,
        });
        return;
      }
      const siblings = blockList.filter(
        (b) => b.task_id === block.task_id && b.id !== blockId && isActiveBlock(b.status),
      );
      const occupied = occupiedForPlacement({ blockId, taskId: block.task_id });

      if (siblings.length > 0) {
        const totalMs = totalBlockDurationMs(blockList, block.task_id);
        if (canRestoreSingleBlock(start, totalMs, occupied)) {
          updateBlock.mutate(
            {
              id: blockId,
              starts_at: start.toISOString(),
              ends_at: new Date(start.getTime() + totalMs).toISOString(),
              clear_split_group: true,
            },
            {
              onSuccess: () => {
                for (const s of siblings) deleteBlock.mutate(s.id);
                warnIfLate(block.task_id, new Date(start.getTime() + totalMs), block.task_title);
              },
              onError: () => arg.revert(),
            },
          );
          return;
        }
      }

      updateBlock.mutate(
        {
          id: blockId,
          starts_at: start.toISOString(),
          ends_at: end.toISOString(),
          clear_split_group: true,
        },
        {
          onSuccess: () => warnIfLate(block.task_id, end, block.task_title),
          onError: () => arg.revert(),
        },
      );
    },
    [
      blockById,
      blockList,
      deleteBlock,
      occupiedForPlacement,
      persistWorkBlockTimes,
      updateBlock,
      updateBlocker,
      warnIfLate,
    ],
  );

  /**
   * After a resize, check the task's total planned time against its estimate.
   *
   * Runs on the numbers the resize is about to produce rather than on refetched
   * data, so the question is asked in the same beat as the gesture.
   */
  const askAboutEstimate = useCallback(
    (block: TimeBlock, start: Date, end: Date) => {
      const task = taskById.get(block.task_id);
      const estimate = task?.estimate_minutes ?? null;
      if (!task || estimate == null || estimate <= 0) return;

      const siblingsMs = blockList
        .filter(
          (b) =>
            b.task_id === block.task_id && b.id !== block.id && isActiveBlock(b.status),
        )
        .reduce(
          (sum, b) =>
            sum + (new Date(b.ends_at).getTime() - new Date(b.starts_at).getTime()),
          0,
        );
      const plannedMs = siblingsMs + (end.getTime() - start.getTime());
      const delta = estimateDelta(estimate, Math.round(plannedMs / 60_000));
      if (delta.outcome === 'match') return;

      setEstimateChoice({
        taskId: block.task_id,
        blockId: block.id,
        taskTitle: block.task_title,
        estimateMinutes: estimate,
        delta,
        remainderSlot:
          delta.outcome === 'under'
            ? findFreeSlot(
                end,
                delta.deltaMinutes * 60_000,
                // The block being resized is about to occupy its new range, so
                // exclude the stale one and add the new.
                mergeIntervals([
                  ...occupiedForPlacement({ blockId: block.id }),
                  { starts_at: start.getTime(), ends_at: end.getTime() },
                ]),
              )
            : null,
        splitGroupId: block.split_group_id,
      });
    },
    [taskById, blockList, occupiedForPlacement],
  );

  const onEventResize = useCallback(
    (arg: EventResizeDoneArg) => {
      dropHandledRef.current = true;
      const id = arg.event.id;
      if (id.startsWith('wb-')) {
        const range = getDroppedRange(arg);
        if (!range) {
          arg.revert();
          return;
        }
        persistWorkBlockTimes(Number(id.slice(3)), range.start, range.end, () => arg.revert());
        return;
      }
      if (id.startsWith('blk-')) {
        const range = getDroppedRange(arg);
        if (!range) {
          arg.revert();
          return;
        }
        const { start, end } = range;
        updateBlocker.mutate(
          {
            id: Number(id.slice(4)),
            starts_at: start.toISOString(),
            ends_at: end.toISOString(),
          },
          { onError: () => arg.revert() },
        );
        return;
      }
      if (!id.startsWith('block-')) {
        arg.revert();
        return;
      }
      const blockId = Number(id.slice(6));
      const range = getDroppedRange(arg);
      if (!range) {
        arg.revert();
        return;
      }
      const { start, end } = range;
      const block = blockById.get(blockId);
      if (!block) {
        arg.revert();
        return;
      }
      if (
        intervalConflicts(
          start,
          end,
          occupiedForPlacement({ blockId, taskId: block.task_id }),
        )
      ) {
        arg.revert();
        setPendingPlacement({
          kind: 'move',
          taskId: block.task_id,
          taskTitle: block.task_title,
          blockId,
          startsAt: start,
          endsAt: end,
        });
        return;
      }
      applyMove(blockId, block.task_id, block.task_title, start, end);
      const siblings = blockList.filter(
        (b) => b.task_id === block.task_id && b.id !== blockId && isActiveBlock(b.status),
      );
      if (siblings.length === 0) {
        const plannedMinutes = Math.round((end.getTime() - start.getTime()) / 60_000);
        if (plannedMinutes >= 5) {
          updateTask.mutate({
            id: block.task_id,
            estimate_minutes: plannedMinutes,
          });
        }
      } else {
        askAboutEstimate(block, start, end);
      }
    },
    [
      applyMove,
      askAboutEstimate,
      blockById,
      blockList,
      occupiedForPlacement,
      persistWorkBlockTimes,
      updateBlocker,
      updateTask,
    ],
  );

  const onEventClick = useCallback(
    (arg: EventClickArg) => {
      const raw = arg.event.id;
      if (raw.startsWith('wb-')) {
        // Locked bands ignore click; unlocked ones are for drag, not a sheet.
        return;
      }
      if (raw.startsWith('evt-')) {
        const id = Number(raw.slice(4));
        const event = eventById.get(id);
        if (event) {
          setSelectedBlockId(null);
          setOpenTaskId(null);
          setSelectedEvent(event);
        }
        return;
      }
      if (raw.startsWith('blk-')) {
        setSelectedEvent(null);
        setSelectedBlockId(null);
        setOpenTaskId(null);
        setEditingBlockerId(Number(raw.slice(4)));
        return;
      }
      if (raw.startsWith('block-')) {
        setSelectedEvent(null);
        const blockId = Number(raw.slice(6));
        setSelectedBlockId(blockId);
        const block = blockById.get(blockId);
        if (block) setOpenTaskId(block.task_id);
      }
    },
    [eventById, blockById],
  );

  const onEventDrag = useCallback(
    (arg: { event: { id: string; start: Date | null; end: Date | null } }) => {
      const start = arg.event.start;
      const end = arg.event.end;
      if (!start || !end) {
        setDragConflicts(false);
        return;
      }
      // A blocker sits inside its own busy shadow, so it would always read as
      // conflicting with itself. Overlapping a meeting is fine for a blocker
      // anyway: it is a statement about availability, not a booking.
      if (arg.event.id.startsWith('blk-')) {
        setDragConflicts(false);
        return;
      }

      let exclude: { blockId?: number; taskId?: number } = {};
      if (arg.event.id.startsWith('block-')) {
        const blockId = Number(arg.event.id.slice(6));
        const block = blockById.get(blockId);
        if (block) exclude = { blockId, taskId: block.task_id };
      }
      setDragConflicts(intervalConflicts(start, end, occupiedForPlacement(exclude)));
    },
    [blockById, occupiedForPlacement],
  );

  const onEventDragStart = useCallback(
    (arg: { event: { id: string; start: Date | null; end: Date | null } }) => {
      beginDrag();
      dropHandledRef.current = false;
      if (arg.event.start && arg.event.end) {
        dragOriginRef.current = {
          id: arg.event.id,
          start: arg.event.start,
          end: arg.event.end,
        };
      } else {
        dragOriginRef.current = null;
      }

      dragMoveCleanupRef.current?.();
      const onMove = (e: PointerEvent) => {
        setBacklogDropActive(pointOverBacklog(e.clientX, e.clientY));
      };
      document.addEventListener('pointermove', onMove);
      dragMoveCleanupRef.current = () => {
        document.removeEventListener('pointermove', onMove);
        setBacklogDropActive(false);
        dragMoveCleanupRef.current = null;
      };
    },
    [beginDrag],
  );

  const onEventDragStop = useCallback(
    (info: EventDragStopArg) => {
      dragMoveCleanupRef.current?.();
      setDragConflicts(false);
      setBacklogDropActive(false);

      const origin = dragOriginRef.current;
      dragOriginRef.current = null;

      if (
        !dropHandledRef.current &&
        origin &&
        info.event.id === origin.id &&
        info.event.start &&
        info.event.start.getTime() !== origin.start.getTime()
      ) {
        onEventDrop({
          event: info.event,
          oldEvent: { ...info.event, start: origin.start, end: origin.end },
          revert: () => {
            info.event.setStart(origin.start);
            info.event.setEnd(origin.end);
          },
          jsEvent: info.jsEvent,
        } as EventDropArg);
      }
      dropHandledRef.current = false;

      const { x, y } = pointerCoords(info.jsEvent);
      if (!hasPointerCoords(info.jsEvent) || !pointOverBacklog(x, y)) {
        afterCalendarGesture(endDrag);
        return;
      }

      const id = info.event.id;
      if (id.startsWith('blk-')) {
        const blocker = blockerById.get(Number(id.slice(4)));
        if (blocker) removeBlocker(blocker);
        afterCalendarGesture(endDrag);
        return;
      }
      if (!id.startsWith('block-')) {
        afterCalendarGesture(endDrag);
        return;
      }

      const block = blockById.get(Number(id.slice(6)));
      if (block) returnBlockToBacklog(block);
      afterCalendarGesture(endDrag);
    },
    [blockById, blockerById, endDrag, onEventDrop, removeBlocker, returnBlockToBacklog],
  );

  // Keyboard: arrows nudge selected block; Delete returns task to backlog.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (selectedBlockId == null) return;
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }

      const block = blockById.get(selectedBlockId);
      if (!block) return;

      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        returnBlockToBacklog(block);
        return;
      }

      const shift = e.shiftKey;
      const deltaMin =
        e.key === 'ArrowUp' || e.key === 'ArrowLeft'
          ? -SNAP_MINUTES
          : e.key === 'ArrowDown' || e.key === 'ArrowRight'
            ? SNAP_MINUTES
            : 0;
      if (!deltaMin) return;
      e.preventDefault();

      const start = DateTime.fromISO(block.starts_at, { zone: 'utc' });
      const end = DateTime.fromISO(block.ends_at, { zone: 'utc' });
      if (shift) {
        const nextEnd = end.plus({ minutes: deltaMin });
        if (nextEnd <= start.plus({ minutes: 5 })) return;
        const nextStart = start.toJSDate();
        const nextEndDate = nextEnd.toJSDate();
        if (
          intervalConflicts(
            nextStart,
            nextEndDate,
            occupiedForPlacement({ blockId: block.id, taskId: block.task_id }),
          )
        ) {
          setPendingPlacement({
            kind: 'move',
            taskId: block.task_id,
            taskTitle: block.task_title,
            blockId: block.id,
            startsAt: nextStart,
            endsAt: nextEndDate,
          });
          return;
        }
        applyMove(block.id, block.task_id, block.task_title, nextStart, nextEndDate);
      } else {
        const nextStart = start.plus({ minutes: deltaMin }).toJSDate();
        const nextEndDate = end.plus({ minutes: deltaMin }).toJSDate();
        if (
          intervalConflicts(
            nextStart,
            nextEndDate,
            occupiedForPlacement({ blockId: block.id, taskId: block.task_id }),
          )
        ) {
          setPendingPlacement({
            kind: 'move',
            taskId: block.task_id,
            taskTitle: block.task_title,
            blockId: block.id,
            startsAt: nextStart,
            endsAt: nextEndDate,
          });
          return;
        }
        applyMove(block.id, block.task_id, block.task_title, nextStart, nextEndDate);
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [applyMove, blockById, occupiedForPlacement, returnBlockToBacklog, selectedBlockId]);

  // External drag: tint busy when the mirror overlaps (best-effort via pointer).
  useEffect(() => {
    let dragging = false;
    const onStart = (e: Event) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest?.('[data-plan-chip], [data-carry-chip]')) dragging = true;
    };
    const onEnd = () => {
      dragging = false;
      setDragConflicts(false);
    };
    const onMove = (e: PointerEvent) => {
      if (!dragging) return;
      const apiCal = calendarRef.current?.getApi();
      if (!apiCal) return;
      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (!el?.closest('.fc-timegrid-body')) {
        setDragConflicts(false);
        return;
      }
      // Approximate slot from Y within the time grid. FC drop handles exact time.
      // Conflict tint uses whether *any* busy exists in view as a coarse signal
      // when we can't resolve the slot; precise check runs on drop.
      setDragConflicts(busyList.length > 0);
    };
    document.addEventListener('pointerdown', onStart);
    document.addEventListener('pointerup', onEnd);
    document.addEventListener('pointercancel', onEnd);
    document.addEventListener('pointermove', onMove);
    return () => {
      document.removeEventListener('pointerdown', onStart);
      document.removeEventListener('pointerup', onEnd);
      document.removeEventListener('pointercancel', onEnd);
      document.removeEventListener('pointermove', onMove);
    };
  }, [busyList.length]);

  const schedulableTasks = useMemo(
    () =>
      (tasks.data ?? []).filter(
        (t) =>
          t.status !== 'done' &&
          t.status !== 'dropped' &&
          // Scheduled work lives on the grid until returned to the backlog.
          t.status !== 'scheduled',
      ),
    [tasks.data],
  );

  /**
   * Unfinished work from before the day on screen.
   *
   * Excludes anything already moved onto this day, the optimistic patch in
   * `useUpdateTimeBlock` rewrites the block's times in place, so filtering on
   * the start is what makes a moved chip leave the list immediately instead of
   * lingering until the refetch lands.
   */
  const carryOverBlocks = useMemo(() => {
    const dayStartMs = range.start.getTime();
    return (carryOver.data ?? []).filter((b) => {
      if (!isActiveBlock(b.status)) return false;
      if (new Date(b.starts_at).getTime() >= dayStartMs) return false;
      const task = taskById.get(b.task_id);
      return !task || (task.status !== 'done' && task.status !== 'dropped');
    });
  }, [carryOver.data, range.start, taskById]);

  /** `tomorrow`, `today`, or `Thu`: how the move action names its destination. */
  const targetDayLabel = useMemo(() => {
    const day = DateTime.fromJSDate(range.start).setZone(timezone).startOf('day');
    const offset = Math.round(day.diff(DateTime.now().setZone(timezone).startOf('day'), 'days').days);
    if (offset === 0) return 'today';
    if (offset === 1) return 'tomorrow';
    if (offset === -1) return 'yesterday';
    return day.toFormat('ccc');
  }, [range.start, timezone]);

  /** One click: same hour, new day. */
  const moveCarryBlock = useCallback(
    (block: TimeBlock) => {
      const { startsAt, endsAt } = carryTarget(block, range.start, timezone);
      queuePlacement({
        kind: 'move',
        blockId: block.id,
        taskId: block.task_id,
        taskTitle: block.task_title,
        startsAt,
        endsAt,
      });
    },
    [queuePlacement, range.start, timezone],
  );

  const listedTask = (tasks.data ?? []).find((t) => t.id === openTaskId) ?? null;
  // A subtask opened from its parent's sheet can be deferred, and so missing
  // from this list. Fetch it rather than closing the sheet under the user.
  const fetchedTask = useTask(listedTask || openTaskId == null ? null : openTaskId);
  const openTask = listedTask ?? fetchedTask.data ?? null;

  /**
   * Delete the task behind a block.
   *
   * A leaf task can be put back exactly as it was, so it gets the undo toast
   * this app uses everywhere else. A task with subtasks cannot (the children
   * go with it), so that case says plainly that it is gone instead of offering
   * an undo that would quietly restore less than it took away.
   */
  const deleteTaskFromMenu = useCallback(
    (block: TimeBlock) => {
      const task = taskById.get(block.task_id);
      const restorable = task != null && task.rollup_total_count <= 1;
      const snapshot = { block: { ...block }, task };

      deleteTask.mutate(block.task_id, {
        onSuccess: () => {
          setSelectedBlockId(null);
          setOpenTaskId(null);

          if (!restorable || !snapshot.task) {
            toast.show(`Deleted "${block.task_title}" and its subtasks`, {
              tone: 'danger',
            });
            return;
          }

          const original = snapshot.task;
          toast.undoable(`Deleted "${original.title}"`, () => {
            createTask.mutate(
              {
                title: original.title,
                notes: original.notes,
                client_id: original.client_id,
                project_id: original.project_id,
                estimate_minutes: original.estimate_minutes,
                priority: original.priority,
                energy: original.energy,
                due_at: original.due_at,
              },
              {
                onSuccess: (restored) =>
                  createBlock.mutate({
                    task_id: restored.id,
                    starts_at: snapshot.block.starts_at,
                    ends_at: snapshot.block.ends_at,
                    status: 'confirmed',
                    origin: snapshot.block.origin,
                  }),
              },
            );
          });
        },
        onError: (error) => toast.error(error.message),
      });
    },
    [taskById, deleteTask, createTask, createBlock],
  );

  /**
   * Actions for whatever was right-clicked.
   *
   * Delete is the only one that varies: a task or blocker is ours to remove,
   * an imported event is not unless its calendar was marked writable. The item
   * stays visible and explains itself rather than vanishing, a menu whose
   * shape changes per row is a menu you cannot learn.
   */
  const menuItems = useMemo((): {
    label: string;
    items: ContextMenuItem[];
  } | null => {
    if (!menu) return null;
    const raw = menu.eventId;

    if (raw.startsWith('wb-')) {
      const wb = workBlockById.get(Number(raw.slice(3)));
      if (!wb) return null;
      const canStart =
        wb.status === 'scheduled' && new Date(wb.ends_at).getTime() > Date.now();
      return {
        label: 'Focus block',
        items: [
          {
            key: 'start',
            label: 'Start now',
            icon: Play,
            onSelect: () => startWorkBlock(wb.id),
            disabled: !canStart,
            reason: canStart ? undefined : 'Block window is over or already active',
          },
          {
            key: 'delete',
            label: 'Remove from plan',
            icon: Trash2,
            tone: 'danger',
            onSelect: () => removeWorkBlock(wb.id),
          },
        ],
      };
    }

    if (raw.startsWith('block-')) {
      const block = blockById.get(Number(raw.slice(6)));
      if (!block) return null;
      const task = taskById.get(block.task_id);
      return {
        label: block.task_title,
        items: [
          {
            key: 'edit',
            label: 'Edit task',
            icon: Pencil,
            onSelect: () => {
              setSelectedBlockId(block.id);
              setOpenTaskId(block.task_id);
            },
          },
          {
            key: 'start',
            label: 'Start timer',
            icon: Play,
            onSelect: () => focusControls.start.mutate({ task_id: block.task_id }),
          },
          {
            key: 'done',
            label: task?.status === 'done' ? 'Mark not done' : 'Mark complete',
            icon: Check,
            onSelect: () => {
              if (task) toggleDone(task);
            },
            disabled: !task,
            reason: task ? undefined : 'Task not loaded yet',
          },
          {
            key: 'unschedule',
            label: 'Back to backlog',
            icon: Undo2,
            onSelect: () => returnBlockToBacklog(block),
          },
          {
            key: 'delete',
            label: 'Delete task',
            icon: Trash2,
            tone: 'danger',
            onSelect: () => deleteTaskFromMenu(block),
          },
        ],
      };
    }

    if (raw.startsWith('blk-')) {
      const blocker = blockerById.get(Number(raw.slice(4)));
      if (!blocker) return null;
      return {
        label: blockerTitle(blocker),
        items: [
          {
            key: 'edit',
            label: 'Edit blocker',
            icon: Pencil,
            onSelect: () => setEditingBlockerId(blocker.id),
          },
          {
            key: 'delete',
            label: 'Delete blocker',
            icon: Trash2,
            tone: 'danger',
            onSelect: () => removeBlocker(blocker),
          },
        ],
      };
    }

    if (raw.startsWith('evt-')) {
      const event = eventById.get(Number(raw.slice(4)));
      if (!event) return null;
      const deletable = deletableCalendarIds.has(event.calendar_id);
      return {
        label: event.title || '(No title)',
        items: [
          {
            key: 'edit',
            label: 'Edit event',
            icon: Pencil,
            onSelect: () => setSelectedEvent(event),
          },
          {
            key: 'done',
            label: event.completed_at ? 'Mark not done' : 'Mark complete',
            icon: Check,
            onSelect: () =>
              toggleEventDone.mutate({
                eventId: event.id,
                done: !event.completed_at,
              }),
          },
          {
            key: 'delete',
            label: 'Delete event',
            icon: Trash2,
            tone: 'danger',
            disabled: !deletable || event.is_recurring,
            reason: !deletable
              ? 'This calendar is read-only in Chronos'
              : event.is_recurring
                ? 'Repeating events are deleted in their own calendar'
                : undefined,
            onSelect: () => {
              const title = event.title || 'event';
              deleteEvent.mutate(event.id, {
                onSuccess: () => {
                  setSelectedEvent(null);
                  toast.show(`Deleted "${title}"`, { tone: 'danger' });
                },
              });
            },
          },
        ],
      };
    }

    return null;
  }, [
    menu,
    blockById,
    blockerById,
    eventById,
    taskById,
    workBlockById,
    deletableCalendarIds,
    focusControls.start,
    toggleDone,
    deleteTaskFromMenu,
    returnBlockToBacklog,
    removeBlocker,
    toggleEventDone,
    deleteEvent,
    startWorkBlock,
    removeWorkBlock,
  ]);

  const openTaskFromChip = useCallback((task: Task) => {
    setSelectedTask(task);
    setOpenTaskId(task.id);
  }, []);

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-4 px-5 py-6">
      <style>{planChipDragStyles}</style>
      <header className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
        <div>
          <h1 className="text-title font-semibold">Plan</h1>
          <p className="mt-1 text-small text-text-secondary">
            Drag onto the grid · drag blocks back to the backlog
          </p>
        </div>
        <div className="flex items-center gap-3">
          {completedCount > 0 && (
            <label className="flex cursor-pointer items-center gap-2 text-small text-text-secondary">
              <input
                type="checkbox"
                checked={hideCompleted}
                onChange={(e) => {
                  setHideCompleted(e.target.checked);
                  localStorage.setItem(HIDE_DONE_KEY, e.target.checked ? '1' : '0');
                }}
                className="size-check accent-[var(--accent)]"
              />
              Hide completed
              <span className="tabular text-text-tertiary">({completedCount})</span>
            </label>
          )}

          <p className="text-small tabular text-text-tertiary">
            {timezone.replace(/_/g, ' ')}
            {showGridError
              ? ' · could not load'
              : events.isPending || busy.isPending || blocks.isPending
                ? ' · loading…'
                : ` · ${(blocks.data ?? []).length} blocks`}
          </p>
        </div>
      </header>

      {/* Past-due placements are easy to make and easy to forget; the count
          stays on screen until they are moved. */}
      {lateBlockIds.size > 0 && (
        <p
          role="status"
          className={cn(
            'flex items-center gap-2 rounded border border-[var(--warning)] bg-warning-quiet',
            'px-3 py-2 text-small text-warning',
          )}
        >
          <AlertTriangle className="size-icon shrink-0" aria-hidden />
          {lateBlockIds.size === 1
            ? '1 block is scheduled after its task is due.'
            : `${lateBlockIds.size} blocks are scheduled after their tasks are due.`}
        </p>
      )}

      {showGridError && (
        <p role="alert" className="text-body text-danger">
          Could not load the plan calendar.{' '}
          <button type="button" className="underline" onClick={retryGrid}>
            Retry
          </button>
        </p>
      )}

      {/*
        Backlog left, calendar right. Explicit height so FullCalendar's
        height="100%" has a real containing block.
      */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-[19rem_minmax(0,1fr)] sm:items-stretch">
        <aside
          data-plan-backlog-drop
          className={cn(
            'order-2 min-w-0 rounded-lg sm:order-1 sm:h-[calc(100dvh-11rem-var(--chrome-bottom,0px))] sm:overflow-y-auto',
            'border border-transparent p-2 -m-2 transition-colors',
            backlogDropActive &&
              'border-[var(--accent-border)] bg-[var(--accent-quiet)] ring-2 ring-[var(--ring)]',
          )}
        >
          <div className="mb-3 border-b border-border pb-2">
            <CalendarVisibility
              accounts={accounts.data ?? []}
              hidden={hiddenCalendars}
              onToggle={toggleCalendar}
              onShowAll={() => persistHidden(new Set())}
            />
          </div>

          <div className="mb-3 border-b border-border pb-3">
            <FocusBlockPalette
              onPick={() => {
                const next = DateTime.now()
                  .setZone(timezone)
                  .plus({ minutes: 30 })
                  .startOf('hour');
                placeFocusBlock(next.toJSDate());
              }}
            />
          </div>

          <div className="mb-3 border-b border-border pb-3">
            <BlockerPalette
              onPick={(kind) => {
                // Keyboard path: land it at the next half hour, then open the
                // sheet so the time can be corrected without a mouse.
                const next = DateTime.now()
                  .setZone(timezone)
                  .plus({ minutes: 30 })
                  .startOf('hour');
                placeBlocker(kind, next.toJSDate());
              }}
            />
          </div>

          <div className="mb-2 border-b border-border pb-2">
            <PlanQuickAdd
              timezone={timezone}
              submitting={createTask.isPending}
              onCapture={captureTask}
              dayStartMinute={dayStart}
            />
          </div>

          {carryOverBlocks.length > 0 && (
            <div className="mb-3 border-b border-border pb-3">
              <PlanCarryOver
                blocks={carryOverBlocks}
                taskById={taskById}
                timezone={timezone}
                targetLabel={targetDayLabel}
                clientColor={colorFor}
                onMove={moveCarryBlock}
                onSelectTask={openTaskFromChip}
                selectedTaskId={selectedTask?.id ?? null}
              />
            </div>
          )}

          <h2 className="mb-2 text-small font-medium text-text-secondary">
            {backlogDropActive
              ? 'Drop to return to backlog'
              : `Backlog · ${schedulableTasks.length}`}
          </h2>
          {tasks.isPending && !tasks.data ? (
            <div className="space-y-2" aria-hidden>
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-12 animate-pulse rounded bg-surface-2" />
              ))}
            </div>
          ) : showBacklogError ? (
            <p role="alert" className="text-body text-danger">
              Could not load backlog.{' '}
              <button type="button" className="underline" onClick={() => void tasks.refetch()}>
                Retry
              </button>
            </p>
          ) : (
            <PlanBacklog
              tasks={schedulableTasks}
              clients={clients.data ?? []}
              projects={projects.data ?? []}
              timezone={timezone}
              sort={backlogSort}
              onSortChange={(next) => {
                setBacklogSort(next);
                localStorage.setItem(SORT_KEY, next);
              }}
              clientColor={colorFor}
              selectedTaskId={selectedTask?.id ?? null}
              onSelectTask={openTaskFromChip}
              onSchedule={setSchedulingTask}
            />
          )}
        </aside>

        <div
          // One listener for the whole grid: FullCalendar re-renders events on
          // every drag, and per-event listeners would have to be re-attached
          // each time.
          onContextMenu={(e) => {
            const el = (e.target as HTMLElement).closest('.fc-event');
            const id = el?.getAttribute('data-event-id');
            if (!id) return;
            e.preventDefault();
            setMenu({ x: e.clientX, y: e.clientY, eventId: id });
          }}
          onPointerDown={(e) => {
            // Touch long-press opens the same actions menu as right-click.
            // Delay sits above FullCalendar's drag long-press (350ms) so a
            // hold-to-drag still wins when the finger moves.
            if (e.pointerType !== 'touch') return;
            const el = (e.target as HTMLElement).closest('.fc-event');
            const id = el?.getAttribute('data-event-id');
            if (!id) return;
            const startX = e.clientX;
            const startY = e.clientY;
            const eventId = id;
            if (longPressTimer.current != null) window.clearTimeout(longPressTimer.current);
            longPressTimer.current = window.setTimeout(() => {
              longPressTimer.current = null;
              setMenu({ x: startX, y: startY, eventId });
            }, 550);
            const clear = () => {
              if (longPressTimer.current != null) {
                window.clearTimeout(longPressTimer.current);
                longPressTimer.current = null;
              }
            };
            const onMove = (ev: PointerEvent) => {
              if (Math.hypot(ev.clientX - startX, ev.clientY - startY) > 10) clear();
            };
            const target = e.currentTarget;
            target.addEventListener('pointerup', clear, { once: true });
            target.addEventListener('pointercancel', clear, { once: true });
            target.addEventListener('pointermove', onMove);
            const done = () => target.removeEventListener('pointermove', onMove);
            target.addEventListener('pointerup', done, { once: true });
            target.addEventListener('pointercancel', done, { once: true });
          }}
          className={
            cn(dragConflicts && 'chronos-drag-conflict') +
            ' chronos-calendar order-1 min-h-[28rem] min-w-0 sm:order-2 ' +
            'overflow-hidden rounded-lg border border-border bg-surface-1 p-2 sm:p-3 ' +
            'h-[min(calc(70dvh-var(--chrome-bottom,0px)),52rem)] sm:h-[calc(100dvh-11rem-var(--chrome-bottom,0px))]'
          }
        >
          <FullCalendar
            ref={calendarRef}
            plugins={[luxonPlugin, timeGridPlugin, dayGridPlugin, interactionPlugin]}
            // One day, and it is tomorrow's: planning starts by filling the
            // next day, and a whole week of columns is seven days of choices
            // when only one of them is the question.
            initialView="timeGridDay"
            initialDate={initialDate}
            headerToolbar={
              compact
                ? { left: 'prev,next', center: 'title', right: 'today' }
                : {
                    left: 'prev,next today',
                    center: 'title',
                    right: 'dayGridMonth,timeGridWeek,timeGridDay',
                  }
            }
            height="100%"
            timeZone={timezone}
            nowIndicator
            allDaySlot
            slotMinTime={minutesToSlot(dayWindow.slotMin)}
            slotMaxTime={minutesToSlot(dayWindow.slotMax)}
            scrollTime={dayWindow.scrollTime}
            slotDuration={`00:${String(SNAP_MINUTES).padStart(2, '0')}:00`}
            // Snap-fine slots, human-readable labels: rows every 15 minutes,
            // times written only on the hour and half hour.
            slotLabelInterval="00:30:00"
            snapDuration={`00:${String(SNAP_MINUTES).padStart(2, '0')}:00`}
            // Side-by-side columns for overlaps, default FC behavior stacks them.
            slotEventOverlap={false}
            weekends
            {...CALENDAR_DRAG_PROPS}
            events={displayEvents}
            datesSet={onDatesSet}
            eventClick={onEventClick}
            editable
            droppable
            drop={onDrop}
            eventDrop={onEventDrop}
            eventResize={onEventResize}
            eventDragStart={(arg) => {
              onEventDragStart(arg);
              onEventDrag(arg);
            }}
            eventDragStop={onEventDragStop}
            eventAllow={(span, moving) => {
              if (!moving?.id.startsWith('block-')) return true;
              const start = span.start;
              const end = span.end;
              if (!start || !end) return true;
              const blockId = Number(moving.id.slice(6));
              const block = blockById.get(blockId);
              const exclude = block ? { blockId, taskId: block.task_id } : { blockId };
              const conflict = intervalConflicts(
                start,
                end,
                occupiedForPlacement(exclude),
              );
              setDragConflicts(conflict);
              return true;
            }}
            // Drag an empty range to capture work straight onto it, the way a
            // calendar creates an event.
            selectable
            selectMirror
            selectMinDistance={4}
            select={(arg) => {
              if (arg.allDay) {
                calendarRef.current?.getApi().unselect();
                return;
              }
              createTaskOnSlot({ start: arg.start, end: arg.end });
              calendarRef.current?.getApi().unselect();
            }}
            // FullCalendar does not put its id in the DOM; the context menu
            // needs it to know what was clicked.
            eventDidMount={(arg) => {
              arg.el.setAttribute('data-event-id', arg.event.id);
            }}
          />
        </div>
      </div>

      <AnimatePresence>
        {openTask && (
          <TaskDetail
            task={openTask}
            onClose={() => setOpenTaskId(null)}
            onOpenTask={setOpenTaskId}
          />
        )}
      </AnimatePresence>

      {selectedBlockId != null && blockById.get(selectedBlockId) && (
        <BlockActions
          block={blockById.get(selectedBlockId)!}
          dueAt={taskById.get(blockById.get(selectedBlockId)!.task_id)?.due_at ?? null}
          timezone={timezone}
          taskDone={taskById.get(blockById.get(selectedBlockId)!.task_id)?.status === 'done'}
          onClose={() => setSelectedBlockId(null)}
          onToggleDone={() => {
            const block = blockById.get(selectedBlockId)!;
            const task = taskById.get(block.task_id);
            if (task) toggleDone(task);
          }}
          onConfirm={
            blockById.get(selectedBlockId)!.status === 'proposed'
              ? () =>
                  confirmBlock.mutate(selectedBlockId, {
                    onSuccess: () => toast.show('Block confirmed', { tone: 'success' }),
                  })
              : undefined
          }
          onDelete={() => {
            const block = blockById.get(selectedBlockId)!;
            returnBlockToBacklog(block);
          }}
        />
      )}

      {estimateChoice && (
        <EstimateChoiceSheet
          taskTitle={estimateChoice.taskTitle}
          estimateMinutes={estimateChoice.estimateMinutes}
          delta={estimateChoice.delta}
          remainderSlot={estimateChoice.remainderSlot}
          timezone={timezone}
          onClose={() => setEstimateChoice(null)}
          onUpdateEstimate={() => {
            updateTask.mutate({
              id: estimateChoice.taskId,
              estimate_minutes: estimateChoice.delta.plannedMinutes,
            });
            setEstimateChoice(null);
          }}
          onScheduleRemainder={() => {
            const slot = estimateChoice.remainderSlot;
            if (!slot) return;

            // Both halves have to carry the same group, or moving one back
            // over the other cannot recognise them as one task again.
            const groupId = estimateChoice.splitGroupId ?? newSplitGroupId();
            if (!estimateChoice.splitGroupId) {
              updateBlock.mutate({
                id: estimateChoice.blockId,
                split_group_id: groupId,
              });
            }

            createBlock.mutate({
              task_id: estimateChoice.taskId,
              starts_at: slot.startsAt.toISOString(),
              ends_at: slot.endsAt.toISOString(),
              status: 'confirmed',
              origin: 'manual_drag',
              // Same task continued, not a second task in the backlog.
              is_continuation: true,
              split_group_id: groupId,
            });
            setEstimateChoice(null);
          }}
        />
      )}

      {menu && menuItems && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          label={menuItems.label}
          items={menuItems.items}
          onClose={() => setMenu(null)}
        />
      )}

      {editingBlockerId != null && blockerById.get(editingBlockerId) && (
        <BlockerSheet
          blocker={blockerById.get(editingBlockerId)!}
          timezone={timezone}
          saving={updateBlocker.isPending}
          onClose={() => setEditingBlockerId(null)}
          onDelete={() => removeBlocker(blockerById.get(editingBlockerId)!)}
          onSave={(patch) => {
            updateBlocker.mutate(
              { id: editingBlockerId, ...patch },
              { onSuccess: () => setEditingBlockerId(null) },
            );
          }}
        />
      )}

      {selectedEvent && (
        <PlanEventSheet
          event={selectedEvent}
          timezone={timezone}
          onClose={() => setSelectedEvent(null)}
        />
      )}

      {schedulingTask && (
        <ScheduleSheet
          task={schedulingTask}
          timezone={timezone}
          submitting={createBlock.isPending}
          onClose={() => setSchedulingTask(null)}
          onSubmit={(startsAt, endsAt) => {
            queuePlacement({
              kind: 'create',
              taskId: schedulingTask.id,
              taskTitle: schedulingTask.title,
              startsAt: new Date(startsAt),
              endsAt: new Date(endsAt),
            });
            setSchedulingTask(null);
          }}
        />
      )}

      {pendingPlacement && (
        <ConflictChoiceSheet
          taskTitle={pendingPlacement.taskTitle}
          startsAt={pendingPlacement.startsAt}
          endsAt={pendingPlacement.endsAt}
          timezone={timezone}
          canSplit={
            splitAroundOccupied(
              pendingPlacement.startsAt,
              pendingPlacement.endsAt,
              occupiedForPlacement(
                pendingPlacement.kind === 'move'
                  ? {
                      blockId: pendingPlacement.blockId,
                      taskId: pendingPlacement.taskId,
                    }
                  : {},
              ),
            ) != null
          }
          onClose={() => setPendingPlacement(null)}
          onChoose={(choice) => {
            if (choice.mode === 'overlap') commitOverlap(pendingPlacement);
            else void commitSplit(pendingPlacement);
          }}
        />
      )}
    </div>
  );
}

function BlockActions({
  block,
  dueAt,
  timezone,
  taskDone,
  onClose,
  onDelete,
  onConfirm,
  onToggleDone,
}: {
  block: TimeBlock;
  dueAt: string | null;
  timezone: string;
  taskDone?: boolean;
  onClose: () => void;
  onDelete: () => void;
  onConfirm?: () => void;
  onToggleDone?: () => void;
}) {
  const start = DateTime.fromISO(block.starts_at).toLocal();
  const end = DateTime.fromISO(block.ends_at).toLocal();
  const due = dueInfo(dueAt, timezone);
  const late = isLate(dueAt, block.ends_at);
  const completed = block.status === 'completed' || taskDone;
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-surface-1 px-4 py-3"
      role="status"
    >
      <div>
        <p className="text-small font-medium text-text">{block.task_title}</p>
        <p className="text-small text-text-secondary">
          {start.toFormat('ccc, LLL d · h:mm a')} – {end.toFormat('h:mm a')}
          {block.status === 'proposed' && ' · proposed'}
          {completed && ' · done'}
          {block.write_error && (
            <span className="text-danger"> · write-back: {block.write_error}</span>
          )}
        </p>
        {due.at && (
          <p
            className={cn(
              'mt-0.5 text-small',
              late ? 'text-warning' : 'text-text-tertiary',
            )}
          >
            {late ? (
              <>
                <AlertTriangle
                  className="mr-1 inline size-icon align-[-2px]"
                  aria-hidden
                />
                Ends {lateBy(dueAt!, block.ends_at, timezone)} after{' '}
                {due.full.toLowerCase()}
              </>
            ) : (
              due.full
            )}
          </p>
        )}
      </div>
      <div className="flex gap-2">
        {onConfirm && (
          <Button variant="primary" onClick={onConfirm}>
            Confirm
          </Button>
        )}
        {onToggleDone && (
          <Button variant="secondary" onClick={onToggleDone}>
            {completed ? 'Mark not done' : 'Mark complete'}
          </Button>
        )}
        <Button variant="ghost" onClick={onDelete}>
          Back to backlog
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
}
